import Component from '@glimmer/component';

export default class ProxyviewsSvgChart extends Component {
  get data() {
    return this.args.data || [];
  }

  get hasData() {
    return this.data.length > 0;
  }

  get width() {
    return this.args.width || 800;
  }

  get height() {
    return this.args.height || 250;
  }

  get metricKey() {
    return this.args.metricKey || 'avg_response_time_ms';
  }

  get paddingX() {
    return 48;
  }

  get paddingY() {
    return 25;
  }

  get graphHeight() {
    return this.height - this.paddingY * 2;
  }

  get graphWidth() {
    return this.width - this.paddingX - 16;
  }

  get bottomY() {
    return this.paddingY + this.graphHeight;
  }

  get bottomTextY() {
    return this.bottomY + 4;
  }

  get graphPoints() {
    if (!this.hasData) return [];

    let sortedData = [...this.data].reverse();
    const values = sortedData.map((d) => Number(d[this.metricKey]) || 0);
    const maxVal = Math.max(...values, 10);
    const minVal = 0;

    const stepX = this.graphWidth / (values.length > 1 ? values.length - 1 : 1);

    return values.map((val, i) => {
      const x = this.paddingX + i * stepX;
      const normalizedY = (val - minVal) / (maxVal - minVal);
      const y = this.paddingY + this.graphHeight - normalizedY * this.graphHeight;
      return {
        x,
        y,
        val: val.toFixed(0),
        tested_at: sortedData[i]?.tested_at,
      };
    });
  }

  get points() {
    return this.graphPoints.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  }

  get areaPoints() {
    if (!this.hasData || this.graphPoints.length === 0) return '';
    const firstX = this.graphPoints[0].x.toFixed(1);
    const lastX = this.graphPoints[this.graphPoints.length - 1].x.toFixed(1);
    const bottomYStr = this.bottomY.toFixed(1);
    return `${firstX},${bottomYStr} ${this.points} ${lastX},${bottomYStr}`;
  }

  get maxLabel() {
    if (!this.hasData) return '0';
    const values = this.data.map((d) => Number(d[this.metricKey]) || 0);
    return Math.max(...values).toFixed(0);
  }

  <template>
    <div class="pd-svg-chart-container" style="width: 100%; max-width: 100%; overflow-x: auto;">
      {{#if this.hasData}}
        <svg
          viewBox="0 0 {{this.width}} {{this.height}}"
          width="100%"
          height="100%"
          preserveAspectRatio="none"
          style="min-width: 400px; display: block; overflow: visible;"
        >
          <defs>
            <linearGradient id="pdChartGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#2563eb" stop-opacity="0.25" />
              <stop offset="100%" stop-color="#2563eb" stop-opacity="0.0" />
            </linearGradient>
          </defs>

          <!-- Horizontal Grid Lines -->
          <line
            x1={{this.paddingX}}
            y1={{this.paddingY}}
            x2={{this.width}}
            y2={{this.paddingY}}
            stroke="rgba(0,0,0,0.06)"
            stroke-width="1"
            stroke-dasharray="4 4"
          />
          <line
            x1={{this.paddingX}}
            y1={{this.bottomY}}
            x2={{this.width}}
            y2={{this.bottomY}}
            stroke="rgba(0,0,0,0.12)"
            stroke-width="1"
          />

          <!-- Y-axis Labels -->
          <text
            x="0"
            y="29"
            font-size="11"
            font-weight="600"
            fill="#64748b"
            font-family="sans-serif"
          >{{this.maxLabel}}ms</text>
          <text
            x="0"
            y={{this.bottomTextY}}
            font-size="11"
            font-weight="600"
            fill="#64748b"
            font-family="sans-serif"
          >0ms</text>

          <!-- Gradient Area Fill -->
          <polygon points={{this.areaPoints}} fill="url(#pdChartGradient)" />

          <!-- The Main Data Line -->
          <polyline
            points={{this.points}}
            fill="none"
            stroke="#2563eb"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />

          <!-- Interactive Data Point Circles -->
          {{#each this.graphPoints as |p|}}
            <circle
              cx={{p.x}}
              cy={{p.y}}
              r="3.5"
              fill="#ffffff"
              stroke="#2563eb"
              stroke-width="2.5"
            >
              <title>{{p.val}}ms ({{p.tested_at}})</title>
            </circle>
          {{/each}}
        </svg>
      {{else}}
        <div
          class="pd-svg-chart-empty"
          style="padding: 32px; text-align: center; color: var(--primary-medium);"
        >
          No historical benchmark data available
        </div>
      {{/if}}
    </div>
  </template>
}
